// inv/src/blocks/Homepage/LastDealsSection.jsx
import { useEffect, useMemo, useState } from 'react'
import { API_URL } from '../../config'
import { useTranslation } from 'react-i18next'
import { useInitialData } from '../../components/data/DataContext'
import InvestorLogo from '../../components/data/InvestorLogo'
import { Button } from '../../components/ui/button'
import { Mail } from 'lucide-react'
import googleLogo from '../../pages/authentication/google-logo.svg'
import { languagePrefixes } from '../../i18n'
import { Link } from 'react-router-dom'
import Timeline from '../../components/ui/lastdeals1'
import { useDealCardCollection } from '../../hooks/deal-card'
import { useMembershipAccess } from '../../hooks/membership'
import { buildInvestorProfilePath } from '../../components/investor-display/helpers'

const DealDetailsSection = ({ bgColor = 'bg-white' }) => {
  const { t, i18n } = useTranslation()
  const { latestDeals, investorMap: ssrInvestorMap = {} } = useInitialData()
  const { isProfessional } = useMembershipAccess()
  const ssrDeals = Array.isArray(latestDeals) && latestDeals.length > 0 ? latestDeals : null
  const langPrefix = useMemo(() => {
    const currentLanguage = (i18n.language || 'en').split('-')[0]
    const rawPrefix = languagePrefixes[currentLanguage] || ''
    return rawPrefix === '' || rawPrefix.startsWith('/') ? rawPrefix : `/${rawPrefix}`
  }, [i18n.language])

  const withLang = (path) => `${langPrefix}${path.startsWith('/') ? path : `/${path}`}`

  const [data, setData] = useState(ssrDeals ?? [])
  const [loading, setLoading] = useState(ssrDeals === null)
  const [error, setError] = useState(null)
  const [investorMap, setInvestorMap] = useState(ssrInvestorMap)

  const buildInvestorChip = (dbId, idx, total) => {
    const inv = investorMap[dbId]
    if (!inv) return dbId
    const to = buildInvestorProfilePath({ investor: inv, language: i18n.language })

    return (
      <Link key={dbId} to={to} className="inline-flex items-center gap-1 hover:underline">
        <InvestorLogo id={inv.id} country={inv.country} className="w-4 h-4" alt={`${inv.name} logo`} />
        <span>{inv.name}{idx < total - 1 ? '' : ''}</span>
      </Link>
    )
  }

  const { items, surfaceConfig } = useDealCardCollection({
    deals: data,
    surfaceKey: 'homeLatest',
    buildInvestorNode: buildInvestorChip,
  })
  const shouldRefetchDeals =
    Boolean(surfaceConfig.refetchOnProfessionalUpgrade) &&
    isProfessional

  useEffect(() => {
    if (ssrDeals !== null && !shouldRefetchDeals) {
      setLoading(false)
      return
    }
    setLoading(true)
    fetch(`${API_URL}/latest-5-deals-public`, { credentials: 'include' })
      .then(r => r.json())
      .then(res => setData(res.deals || []))
      .catch(e => {
        console.error(e)
        setError(e.message)
      })
      .finally(() => setLoading(false))
  }, [shouldRefetchDeals, ssrDeals])

  useEffect(() => {
    if (!data?.length) return
    // Collect IDs from lead + other
    const ids = [...new Set(
      (data || []).flatMap(d => [
        ...(d.lead_investors || []),
        ...(d.other_investors_participated || []),
      ])
    )]
    const missing = ids.filter(id => !investorMap[id])
    if (!missing.length) return

    Promise.all(
      missing.map(_id =>
        fetch(`${API_URL}/investors-limited/${_id}?by=_id`)
          .then(res => (res.ok ? res.json() : Promise.reject(res)))
          .then(inv => ({ _id, id: inv.id, name: inv.name, country: inv.country }))
          .catch(() => null)
      )
    ).then(results => {
      const updated = { ...investorMap }
      results.forEach(p => {
        if (p) updated[p._id] = { id: p.id, name: p.name, country: p.country }
      })
      setInvestorMap(updated)
    })
  }, [data, investorMap])

  const moreText = t('investorCard.readMore', { defaultValue: 'Read more' })
  const lessText = t('investorCard.showLess', { defaultValue: 'Read less' })

  return (
    <section className={`py-24 ${bgColor}`}>
      <div className="w-[75%] mx-auto max-w-screen-xl">

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8 items-center">
          <div className="md:col-span-3">
            <h2 className="text-4xl mb-4">{t('pageHome.content.t22')}</h2>
            <p className="mt-2 mb-0 md:mb-0 leading-7 text-base text-stone-600">
              {t('pageHome.content.t23')}
            </p>
          </div>

          <div className="md:col-span-2 flex md:justify-end">
            <Button
              href={withLang(`/signup?utm_source=crino-homepage&utm_medium=latest-deals-signup-button`)}
              variant="default"
              size="sm"
              className="shrink-0" // Do not shrink the button
              iconLeft={
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  <img src={googleLogo} alt="Google Logo" className="h-4 w-4" />
                </>
              }
            >
              {t('pageHome.content.t24')}
            </Button>
          </div>
        </div>

        <div className="mt-8">
          {loading ? (
            <div className="h-[200px] flex items-center justify-center text-sm opacity-70">
              Loading...
            </div>
          ) : error ? (
            <p className="text-red-500">Error: {error}</p>
          ) : (
            <>
              {items.length > 0 && (
                <div className="mb-6">
                  <div className="transition-all duration-300 ease-in-out pt-4">
                    <Timeline
                      items={items}
                      className="max-w-full"
                      moreText={moreText}
                      lessText={lessText}
                      recipientAccess={surfaceConfig.recipientCompany.accessMode}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>

      </div>
    </section>
  )
}

export default DealDetailsSection
